import { DestroyRef, inject, Injectable } from '@angular/core';
import { shareReplay } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { App } from '../models';
import { WatchdogService } from './watchdog.service';
import { KVRepository } from '../repositories';
import { ClusterService } from './cluster.service';
import { WebsocketService } from './websocket.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { arrayDiff, arrayDiffIdCompare, isTruthy } from '../utils';
import { FileCacheService } from './file-cache.service';
import { AuthService } from './auth.service';

@Injectable()
export class RestaurantAppService {

  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly watchdogService = inject(WatchdogService);
  private readonly kvRepository = inject(KVRepository);
  private readonly clusterService = inject(ClusterService);
  private readonly fileCacheService = inject(FileCacheService);
  private readonly websocketService = inject(WebsocketService);

  private readonly storageKey = 'restaurantApp';
  private readonly logger = this.watchdogService.tag('Restaurant App', 'green');

  public readonly app$ = this.kvRepository.one$(this.storageKey).pipe(
    map((data) => {
      if (data?.value) {
        return data.value as App;
      }

      return null;
    }),
    shareReplay(1),
  );

  public initialize(): void {
    this.logger.info('Initialize');

    // Initial sync
    this.clusterService.leader$.pipe(
      filter(isTruthy),
      switchMap(() => this.websocketService.messages$),
      filter((response) => response.type === 'tableInfo'),
      map((response) => response.data?.app as App | null),
      switchMap((app) => this.sync$(app)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();

    // Clear table data on logout
    this.clusterService.leader$.pipe(
      filter(isTruthy),
      switchMap(() => this.authService.logouted$),
      switchMap(() => this.clear$()),
      tap(() => {
        this.logger.info('Cleared table data on logout');
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  private sync$(newApp: App | null) {
    return this.app$.pipe(
      take(1),
      switchMap((currentApp) => {
        const currentFiles = currentApp?.instance?.files || [];
        const newFiles = newApp?.instance?.files || [];

        const toDeleteFiles = arrayDiff(currentFiles, newFiles, arrayDiffIdCompare);

        return this.fileCacheService.downloadFiles(
          newFiles.map((file) => (
            {
              url: file.url,
              size: file.size,
              mimeType: file.mimeType,
            }
          )),
        ).pipe(
          switchMap((addedFiles) => this.kvRepository.update$({
            key: this.storageKey,
            value: newApp,
          }).pipe(
            switchMap((update) => this.fileCacheService.bulkDelete(toDeleteFiles.map((file) => file.url)).pipe(
              map((deletedFiles) => (
                {
                  update,
                  addedFiles,
                  deletedFiles,
                }
              )),
            )),
          )),
        );
      }),
    );
  }

  private clear$() {
    return this.app$.pipe(
      take(1),
      switchMap((app) => {
        const files = app?.instance?.files || [];
        return this.fileCacheService.bulkDelete(files.map((file) => file.url));
      }),
      switchMap(() => this.kvRepository.delete$(this.storageKey)),
    );
  }

}
